/**
 * ExportAgents Component
 *
 * A modal component for exporting agent data to Excel.
 * Features include:
 * - Column selection for export
 * - Select/Deselect all functionality
 * - Excel file download
 * - Modal overlay with click-outside closing
 */

import React, { useState } from 'react';
import './ExportAgents.css';
import ReportingService from '@frontend/AppSettings/services/ReportingService';

// Available columns for export
const columnOptions = [
  'Agent ID',
  'First Name',
  'Last Name',
  'Company',
  'Email',
  'Phone',
  'Address',
  'Jurisdiction',
];

interface ExportAgentsProps {
  isOpen?: boolean;
  onClose: () => void;
  config?: any;
  modalData?: Record<string, any>;
}

/**
 * ExportAgents Component
 * Manages the export modal and handles Excel file generation
 */
const ExportAgents: React.FC<ExportAgentsProps> = ({ onClose }) => {
  // State management for column selection
  const [selectedColumns, setSelectedColumns] = useState(columnOptions);

  /**
   * Handler for individual column selection/deselection
   *
   * @param {string} column - The column to toggle
   */
  const handleColumnChange = (column: string) => {
    const newSelection = selectedColumns.includes(column)
      ? selectedColumns.filter((c) => c !== column)
      : [...selectedColumns, column];
    setSelectedColumns(newSelection);
  };

  /**
   * Toggles selection of all columns
   */
  const toggleAllColumns = () => {
    if (selectedColumns.length === columnOptions.length) {
      setSelectedColumns([]);
    } else {
      setSelectedColumns(columnOptions);
    }
  };

  /**
   * Handles the Excel file download process
   * Creates a blob from the response and triggers the download
   */
  const downloadReport = async () => {
    try {
      const response = await ReportingService.getAllAgentsExcel({
        cols: selectedColumns as any,
      });

      // Since response is a Blob directly
      const blob = response as Blob;
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = 'Agents_Report.xlsx';
      document.body.appendChild(a);
      a.click();

      setTimeout(() => {
        window.URL.revokeObjectURL(downloadUrl);
        a.remove();
      }, 100);

      // Close modal after download starts
      onClose();
    } catch (error) {
      console.error('Error downloading report:', error);
    }
  };

  /**
   * Handler for clicking outside the modal
   * Closes the modal if the click is on the overlay
   *
   * @param {Event} event - The click event
   */
  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="reporting-overlay" onClick={handleOverlayClick}>
      <div className="reporting-modal-content">
        {/* Close button */}
        <button
          className="reporting-close-button"
          onClick={onClose}
          aria-label="Close export modal"
        >
          ×
        </button>

        {/* Column selection checkboxes */}
        {columnOptions.map((column, idx) => (
          <label key={idx} className="reporting-checkbox">
            <input
              type="checkbox"
              checked={selectedColumns.includes(column)}
              onChange={() => handleColumnChange(column)}
              className="reporting-visually-hidden"
            />
            <span className="reporting-label">{column}</span>
          </label>
        ))}

        {/* Action buttons */}
        <div className="reporting-button-container">
          <button
            onClick={downloadReport}
            className="Reporting-Download-button"
          >
            Download Report
          </button>
          <button
            onClick={toggleAllColumns}
            className="Reporting-Export-button"
          >
            {selectedColumns.length === columnOptions.length
              ? 'Deselect All'
              : 'Select All'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportAgents;
